/* NotificationsVeilRenderer.js */

'use strict';
const _ = require('lodash');
const InputCheckControl = require('@gluenetworks/input_checkbox_control');
const InputTextControl = require('@gluenetworks/input_text_control');
const OptionSelectionControl = require('@gluenetworks/select_list').Multiple;

class NotificationsVeilRenderer {
  constructor(model) {
    this._model = model;
    this._rootElem = document.createElement('notifications-veil-gl');
    this._rootElem.component = this._model;
    if (this.model.darkTheme) {
      this._rootElem.classList.add('dark');
    }
    this._disabledMask = undefined;
    this._notifyMeTitle = undefined;

    if (this.model.titleText) {
      this._notifyMeTitle = document.createElement('p');
      this._notifyMeTitle.classList.add('notifyTitle');
      this._notifyMeTitle.appendChild(document.createTextNode(this.model.titleText));
    }

    this._notifyMeContainer = document.createElement('notify-container');

    this._notifyMeBlock = document.createElement('div');
    this._notifyMeCheckBox = new InputCheckControl();
    this._notifyMeText = document.createElement('notify-label');
    this._notifyMeText.appendChild(document.createTextNode('Notify me'));
    this._notifyMeBlock.appendChild(this._notifyMeCheckBox.renderer.rootElement);
    this._notifyMeBlock.appendChild(this._notifyMeText);

    this._notifySystemAdminBlock = document.createElement('div');
    this._notifySystemAdminCheckBox = new InputCheckControl();
    this._notifySystemAdminText = document.createElement('notify-label');

    this._notifyOtherBlock = document.createElement('div');
    this._notifyOtherBlock.classList.add('other-recipients-block');
    this._notifyOtherCheckBox = new InputCheckControl();
    this._notifyOtherText = document.createElement('notify-label');
    this._notifyOtherText.appendChild(document.createTextNode('Other recipients: '));
    this._notifyOtherEmailInput = new InputTextControl({
      darkTheme: this.model.darkTheme,
      value: '',
      patternMatchAll: true,
      disabled: true,
      useNativeValidation: this.model.useNativeValidation,
      showErrorMessage: this.model.showErrorMessage,
      validationVerbiage: {pattern: 'Please enter valid emails separated by commas'},
      pattern:
        '^([a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9\\-]+\\.)+([a-zA-Z0-9]{2,4})+([,]?[\\s]*)([,][\\s]*([a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9\\-]+\\.)+([a-zA-Z0-9]{2,4})+([,]?[\\s]*))*$',
    });
    this._notifyOtherEmailInput.renderer.rootElement.classList.add('notify-other-input');
    this._notifyOtherBlock.appendChild(this._notifyOtherCheckBox.renderer.rootElement);
    this._notifyOtherBlock.appendChild(this._notifyOtherText);
    this._notifyOtherBlock.appendChild(this._notifyOtherEmailInput.renderer.rootElement);

    this._currentRecipientsBlock = document.createElement('div');
    this._currentRecipientsBlock.classList.add('hidden');
    this._currentRecipientsBlock.classList.add('current-recipients-list');
    this._currentRecipientsText = document.createElement('notify-label');
    this._currentRecipientsText.appendChild(document.createTextNode('Current recipients: '));
    this._currentRecipientsControl = new OptionSelectionControl({
      darkTheme: this.model.darkTheme,
      disabled: true,
      options: [],
    });
    this._currentRecipientsBlock.appendChild(this._currentRecipientsText);
    this._currentRecipientsBlock.appendChild(this._currentRecipientsControl.renderer.rootElement);

    this._registerListeners();
  }

  /********************************************/
  /* START PRIVATE COMPONENT RENDERER METHODS */
  /********************************************/
  _registerListeners() {
    this._notifyMeCheckBox.on(
      'UserValue',
      (this._notifyMeFlagChanged = this._notifyMeFlagChanged.bind(this))
    );
    this._notifySystemAdminCheckBox.on(
      'UserValue',
      (this._notifySystemAdminFlagChanged = this._notifySystemAdminFlagChanged.bind(this))
    );
    this._notifyOtherCheckBox.on(
      'UserValue',
      (this._notifyOtherFlagChanged = this._notifyOtherFlagChanged.bind(this))
    );
    this._currentRecipientsControl.on(
      'Options',
      (this._currentRecipientsChanged = this._currentRecipientsChanged.bind(this))
    );
    this._notifyOtherEmailInput.on(
      'UserValue',
      (this._notifyOtherEmailInputChanged = this._notifyOtherEmailInputChanged.bind(this))
    );
  }

  _unregisterListeners() {
    this._notifyMeCheckBox.removeListener('UserValue', this._notifyMeFlagChanged);
    this._notifySystemAdminCheckBox.removeListener('UserValue', this._notifySystemAdminFlagChanged);
    this._notifyOtherCheckBox.removeListener('UserValue', this._notifyOtherFlagChanged);
    this._currentRecipientsControl.removeListener('Options', this._currentRecipientsChanged);
    this._notifyOtherEmailInput.removeListener('UserValue', this._notifyOtherEmailInputChanged);
  }

  _checkForImpliedConfirm(event) {
    if (event.keyCode === 13) {
      this._callStartCapture();
    }
  }

  /*******************************************/
  /* END PRIVATE COMPONENT RENDERER METHODS  */
  /* START PUBLIC COMPONENT RENDERER METHODS */
  /*******************************************/
  shutdown() {
    this._unregisterListeners();
    let promArr = [
      this._notifyOtherEmailInput.shutdown(),
      this._notifySystemAdminCheckBox.shutdown(),
      this._notifyMeCheckBox.shutdown(),
      this._notifyOtherCheckBox.shutdown(),
      this._currentRecipientsControl.shutdown(),
    ];

    return Promise.all(promArr).then(
      () => {
        this._msgComponent = undefined;
        this._disabledMask = undefined;
        this._notifyMeTitle.remove();
        this._notifyMeTitle = undefined;
        this._notifyOtherEmailInput = undefined;
        this._notifySystemAdminBlock.remove();
        this._notifySystemAdminBlock = undefined;
        this._notifySystemAdminText.remove();
        this._notifySystemAdminText = undefined;
        this._notifyMeBlock.remove();
        this._notifyMeBlock = undefined;
        this._notifyMeText.remove();
        this._notifyMeText = undefined;
        this._notifyOtherBlock.remove();
        this._notifyOtherBlock = undefined;
        this._notifyOtherText.remove();
        this._notifyOtherText = undefined;
        this._notifySystemAdminCheckBox = undefined;
        this._notifyMeCheckBox = undefined;
        this._notifyOtherCheckBox = undefined;
        this._notifyMeContainer.remove();
        this._notifyMeContainer = undefined;
        this._currentRecipientsBlock.remove();
        this._currentRecipientsBlock = undefined;
        this._currentRecipientsText.remove();
        this._currentRecipientsText = undefined;
        this._currentRecipientsControl = undefined;
        this._rootElem.remove();
        this._rootElem = undefined;
        this._model = undefined;
      },
      (err) => {
        this._model.logger.warn(err, 'NotificationsVeilRenderer.shutdown() error');
      }
    );
  }

  /**
   * Stateless method to completely rebuild this component from the _rootElem down.
   * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
   * Promise is returned the caller will assume the Renderer has completed rendering.
   */
  render() {
    return this.model.whenModelIsReady().then(() => {
      if (!this._rootElem.hasChildNodes()) {
        let renderPromises = [this._notifyMeCheckBox.renderer.render()];
        return Promise.all(renderPromises).then(() => {
          if (this.model.disabled) {
            this._rootElem.classList.add('disabled');
            this._disabledMask = document.createElement('disabled-mask-gl');
            this._rootElem.appendChild(this._disabledMask);
          }
          if (this._notifyMeTitle) {
            this._rootElem.appendChild(this._notifyMeTitle);
          }
          this._rootElem.appendChild(this._notifyMeContainer);

          // This block should be defined after we got actionGroup values ready
          this._notifySystemAdminText.appendChild(
            document.createTextNode(this.model._actionGroup.name + ' recipients list')
          );
          this._notifySystemAdminBlock.appendChild(
            this._notifySystemAdminCheckBox.renderer.rootElement
          );
          this._notifySystemAdminBlock.appendChild(this._notifySystemAdminText);
          this._setInputValues();

          this._notifyMeContainer.appendChild(this._notifyMeBlock);
          this._notifyMeContainer.appendChild(this._notifySystemAdminBlock);
          this._notifyMeContainer.appendChild(this._notifyOtherBlock);
          this._notifyMeContainer.appendChild(this._currentRecipientsBlock);
        });
      }
      Promise.resolve();
    });
  }

  get rootElement() {
    return this.getRootElement();
  }

  getRootElement() {
    return this._rootElem;
  }

  get model() {
    return this.getModel();
  }

  getModel() {
    return this._model;
  }

  /*******************************************/
  /* END COMPONENT RENDERER METHODS */
  /* START PRIVATE RENDER METHODS */
  /*******************************************/

  /**
   * set input, and checkboxes values, using model predefined values
   */
  _setInputValues() {
    let emailListToShow = [];
    Promise.resolve()
      .then(() => {
        if (
          this._model._scheduleCreator &&
          this._model._userName !== this._model._scheduleCreator
        ) {
          this._notifyMeCheckBox.setDisabled(true);
        }
        if (
          this.model.notifyMe === this.model.userId ||
          this.model._scheduleCreator === this.model._userName
        ) {
          this.model._notifyMeEmail = this.model.userEmail;
        }
        if (this.model.notifyMe) {
          this._notifyMeCheckBox.setValue(true);
          if (this.model.notifyMe === this.model.userId) {
            emailListToShow.push(this.model._notifyMeEmail);
            return Promise.resolve(emailListToShow);
          } else {
            return this.model.pushCreatorEmail(emailListToShow, this.model.notifyMe);
          }
        }
        return emailListToShow;
      })
      .then((emailListToShow) => {
        if (this.model.notifyActionGroupDefault) {
          this._notifySystemAdminCheckBox.setValue(true);
          if (this.model.isSystemNotificationListNotEmpty()) {
            emailListToShow = emailListToShow.concat(this.model.systemNotificationList);
          }
        }
        if (this.model.notifyEmail && this.model.notifyEmail.length > 0) {
          const notifyEmailAsString = this.model.notifyEmail.join(', ');
          this._notifyOtherCheckBox.setValue(true);
          this._notifyOtherEmailInput.setValue(notifyEmailAsString);
          this._notifyOtherEmailInput.disabled = false;
          emailListToShow = emailListToShow.concat(this.model.notifyEmail);
        }
        if (emailListToShow.length > 0) {
          this._addEmailsToTheListControl(_.uniq(emailListToShow));
        }
      });
  }

  /**
   * fully rebuild email list from current input, and checkboxes values,
   * + validating each email from the input field
   */
  _rebuildEmailList() {
    const emailValidation =
      /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+([,]?[\s]*)$/;
    let clearedEmailList = [];
    Promise.resolve()
      .then(() => {
        if (this._notifyMeCheckBox.value) {
          this.model.notifyMe = this.model.userId;
          if (!this._model._notifyMeEmail) {
            return this._model.pushCreatorEmail(clearedEmailList, this.model.notifyMe);
          }
          clearedEmailList.push(this.model._notifyMeEmail);
          return clearedEmailList;
        } else {
          this.model.notifyMe = null;
          return clearedEmailList;
        }
      })
      .then((emailList) => {
        clearedEmailList = emailList;
        if (this._notifySystemAdminCheckBox.value) {
          const notifEmails = this.model.systemNotificationList;
          clearedEmailList = _.union(clearedEmailList, notifEmails);
        }

        if (this._notifyOtherCheckBox.value) {
          let inputEmailList = this._notifyOtherEmailInput.value
            ? this._notifyOtherEmailInput.value
                .split(',')
                .filter((email) => email.trim().length > 0 && emailValidation.test(email.trim()))
                .map((email) => email.trim())
            : [];
          clearedEmailList = _.union(clearedEmailList, inputEmailList);
        }
        this._currentRecipientsControl.options = [];
        this._addEmailsToTheListControl(clearedEmailList);
      });
  }

  /**
   * Add emails to the email list control
   * @param {Array} emailListToAdd
   */
  _addEmailsToTheListControl(emailListToAdd) {
    emailListToAdd.forEach((email) => {
      if (email.length > 0) {
        this._currentRecipientsControl.options.add({label: email, value: email});
      }
    });
    this._currentRecipientsControl.setOptions(this._currentRecipientsControl.options);
    this.model._emailListWasChanged(this._currentRecipientsControl.options);
  }

  /*******************************************/
  /* END PRIVATE RENDER METHODS */
  /* START HANDLER METHODS */
  /*******************************************/

  /*******************************************/
  /* END HANDLER METHODS */
  /* START PRIVATE API METHODS */
  /*******************************************/
  _notifyMeFlagChanged() {
    this._rebuildEmailList();
  }

  _notifySystemAdminFlagChanged() {
    if (this.model.isSystemNotificationListNotEmpty()) {
      this._rebuildEmailList();
      //if we have email list empty, just emit that the value was changed
    } else {
      this.model._emailListWasChanged(this._currentRecipientsControl.options);
    }
  }

  /**
   * @param {boolean} isFlagSet
   */
  _notifyOtherFlagChanged(isFlagSet) {
    this._notifyOtherEmailInput.disabled = !isFlagSet;
    this._rebuildEmailList();
  }

  _notifyOtherEmailInputChanged() {
    if (this._notifyOtherCheckBox.value === true) {
      this.model._validationStateChanged(!this._notifyOtherEmailInput.invalid);
      this._rebuildEmailList();
    }
  }

  /**
   * @param {Array} value
   */
  _currentRecipientsChanged(value) {
    if (value && value.length > 0) {
      this._currentRecipientsBlock.classList.remove('hidden');
    } else {
      this._currentRecipientsBlock.classList.add('hidden');
    }
  }

  /*******************************************/
  /*  END PRIVATE API METHODS */
  /*******************************************/
}

module.exports = NotificationsVeilRenderer;
